import React, { useState, useEffect } from 'react';
import { DatabaseService } from '../../services/DatabaseService';
import { STVCalculator, generateSTVReport, exportToCSV } from '../../utils/STVCalculator';

const ResultsView = ({ pollId }) => {
  // State management
  const [results, setResults] = useState(null);   // Calculated poll results
  const [loading, setLoading] = useState(true);   // Loading state
  const [error, setError] = useState('');         // Error messages
  const [exportReady, setExportReady] = useState(false); // CSV export state
  const [csvData, setCsvData] = useState('');     // CSV data for export
  
  // Fetch and calculate poll results when component mounts
  useEffect(() => {
    const fetchResults = async () => {
      try {
        // Get raw poll data and votes
        const data = await DatabaseService.getPollResults(pollId);
        
        // If no votes, show empty results
        if (!data.votes || data.votes.length === 0) {
          setResults({
            winners: [],
            rounds: [],
            noVotes: true
          });
          return;
        }
        
        // Calculate results using STV algorithm
        const calculator = new STVCalculator(data.votes, data.pollData.options);
        const stvResults = calculator.calculate();
        
        // Generate formatted report
        const report = generateSTVReport(stvResults, data.pollData.options);
        
        // Generate CSV for export
        const csv = exportToCSV(report);
        setCsvData(csv);
        setExportReady(true);
        
        // Store results for display
        setResults({
          ...report,
          pollData: data.pollData
        });
      } catch (err) {
        setError('Failed to load results: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchResults();
  }, [pollId]);
  
  // Handle CSV file download
  const downloadCSV = () => {
    if (!exportReady) return;
    
    // Create a download link for the CSV data
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `poll-results-${pollId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }
  
  // Show error message
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }
  
  // Show message if no results available
  if (!results) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
        <span className="block sm:inline">No results available</span>
      </div>
    );
  }
  
  // Show empty state if no votes yet
  if (results.noVotes) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
          <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="mt-3 text-lg leading-6 font-medium text-gray-900">
          No votes yet
        </h3>
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            This poll hasn't received any votes yet. Share the poll link with others to start collecting votes.
          </p>
        </div>
      </div>
    );
  }
  
  // Main results display
  return (
    <div>
      {/* Results header with export button */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">Results</h2>
        
        {exportReady && (
          <button
            onClick={downloadCSV}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export Report
          </button>
        )}
      </div>
      
      {/* STV explanation box */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">About STV (Single Transferable Vote)</h3>
        <p className="text-sm text-blue-700">
          STV is a ranked choice voting system that minimizes wasted votes and ensures that the winner has broad support. 
          In this system, voters rank candidates in order of preference. If a candidate reaches the quota, they win. Any surplus 
          votes are redistributed based on the next preferences. If no candidate reaches the quota, the candidate with the fewest 
          votes is eliminated, and their votes are redistributed.
        </p>
      </div>
      
      {/* Winner section */}
      <div className="mb-6">
        <h3 className="font-medium text-gray-900 mb-3">Winner</h3>
        
        {results.winners.length > 0 ? (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-green-800">{results.winners[0].name}</h3>
                <p className="text-sm text-green-700">
                  Won with the highest support after {results.rounds.length} round{results.rounds.length !== 1 ? 's' : ''} of counting.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-700">
              No winner determined yet. This could be due to a tie or insufficient votes.
            </p>
          </div>
        )}
      </div>
      
      {/* Election summary section */}
      <div className="mb-6">
        <h3 className="font-medium text-gray-900 mb-3">Election Summary</h3>
        <div className="bg-white shadow overflow-hidden rounded-md">
          <ul className="divide-y divide-gray-200">
            <li className="px-4 py-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Total Votes:</span>
                <span className="text-sm font-medium text-gray-900">{results.totalVotes}</span>
              </div>
            </li>
            <li className="px-4 py-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Quota to Win:</span>
                <span className="text-sm font-medium text-gray-900">{results.quota}</span>
              </div>
            </li>
            <li className="px-4 py-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Total Rounds:</span>
                <span className="text-sm font-medium text-gray-900">{results.rounds.length}</span>
              </div>
            </li>
            <li className="px-4 py-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Report Generated:</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(results.timestamp).toLocaleString()}
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>
      
      {/* Rounds breakdown table */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Rounds Breakdown</h3>
        <div className="overflow-x-auto bg-white shadow rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Round
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Map through all voting rounds */}
              {results.rounds.map((round, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Round {round.round}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {round.winners.length > 0 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Winner elected
                      </span>
                    ) : round.eliminated ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Option eliminated
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        No outcome
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                    {round.winners.length > 0 && (
                      <span>Winner: {round.winners.join(', ')}</span>
                    )}
                    {round.eliminated && (
                      <span>Eliminated: {round.eliminated}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResultsView;